import React, {useEffect} from 'react'
import {useMutation} from '@apollo/client'
import {Link, useLocation} from 'react-router-dom'
import AppIcon from '../../components/AppIcon'
import AppImage from '../../components/AppImage'
import Button from '../../components/Button'
import Counter from '../../components/Counter'
import Drawer from '../../components/Drawer'
import FormattedPrice from '../../components/FormattedPrice'
import IconButton from '../../components/IconButton'
import LinkButton from '../../components/LinkButton'
import {LoadingConsumer} from '../../components/LoadingWrapper'
import Text from '../../components/Text'
import {trackPurchase} from '../../services/analytics'
import {CART_LINES_UPDATE, groupLineItems, useCart} from '../../services/shopify'
import './styles.scss'

export default function Cart() {
  const {pathname} = useLocation()
  const {cart, open, toggleCart, updateCart} = useCart()
  const [cartLinesUpdate] = useMutation(CART_LINES_UPDATE)
  const isEmpty = !cart || cart.lines.length === 0
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => toggleCart(false), [pathname])
  const lineItemGroups = groupLineItems(isEmpty ? [] : cart.lines)
  return <Drawer
    className="Cart"
    open={open}
    position="right"
    onOverlayClick={() => toggleCart(false)}>
    <div className="Cart__topBar">
      <Text styleVariant="heading2">Your cart</Text>
      <IconButton icon="clear" onClick={() => toggleCart(false)} />
    </div>
    <div className="Cart__body">
      {isEmpty ? <Text className="Cart__emptyMessage">Your cart is empty.</Text> : <>{lineItemGroups
        .map(lineItems => <LineItemGroup
          key={lineItems.map(item => item.id).reduce((acc, curr) => acc + curr, '')}
          cartId={cart.id}
          cartLinesUpdate={cartLinesUpdate}
          updateCart={updateCart}
          lineItems={lineItems}
          quantity={lineItems[0].quantity} />
        )}
      </>}
      <Button
        styleVariant="tertiary"
        onClick={() => toggleCart(false)}>Add items</Button>
    </div>
    <div className="Cart__footer">
      {isEmpty ? null : <Text
        className="Cart__total"
        styleVariant="heading2">
        <div>Total</div>
        <FormattedPrice>{cart.cost.subtotalAmount.amount}</FormattedPrice>
      </Text>}
      <LoadingConsumer>
        {({addLoad}) => <Button
          className="Cart__checkoutButton"
          disabled={isEmpty}
          onClick={() => {
            addLoad('checkout')
            trackPurchase(
              parseFloat(cart.cost.subtotalAmount.amount),
              cart.cost.subtotalAmount.currencyCode,
              null,
              () => {
                window.location.href = cart.checkoutUrl
              }
            )
          }}>Checkout</Button>}
      </LoadingConsumer>
      <div className="Cart__deliveryMessage">
        <AppIcon className="Cart__deliveryIcon" icon="truck" />
        <Text styleVariant="heading3">Free delivery on all orders.</Text>
      </div>
    </div>
  </Drawer>
}

function LineItemGroup({cartId, cartLinesUpdate, updateCart, lineItems, quantity}) {
  const notes = lineItems
    .map(item => (item.attributes.find(attr => attr.key === 'Notes') || {}).value)
    .filter(it => !!it)[0]
  return <div className="Cart__lineItem">
    {lineItems.map(lineItem => {
      const attributes = lineItem.attributes
        .filter(attr => attr.key !== 'Design ID' && attr.key !== 'Notes')
        .sort((a, b) => a.key.localeCompare(b.key))
      return <div
        key={lineItem.id}
        className="Cart__lineItemTop">
        <AppImage
          src={(lineItem.merchandise.image || {}).url}
          width={64}
          height={64}
          radius={12}
          bordered/>
        <div className="Cart__lineItemBody">
          <Text styleVariant="heading3">
            <Link
              className="Cart__lineItemLink"
              to={`/products/${lineItem.merchandise.product.handle}?v=${lineItem.merchandise.id.split('/').pop()}`}>
              {lineItem.merchandise.product.title}
            </Link>
          </Text>
          {lineItem.merchandise.title === 'Default Title' ? null :
            <Text styleVariant="body3">{lineItem.merchandise.title}</Text>}
          {!attributes.length ? null : attributes.map(attr => {
            return <Text key={attr.key} styleVariant="body2">{attr.key}: {attr.value}</Text>
          })}
          <Text styleVariant="body3">
            <FormattedPrice compareAt={(lineItem.merchandise.compareAtPrice || {}).amount}>
              {lineItem.merchandise.price.amount}
            </FormattedPrice>
          </Text>
        </div>
        <Text styleVariant="heading3">
          <FormattedPrice compareAt={lineItem.quantity * (lineItem.merchandise.compareAtPrice || {}).amount}>
            {lineItem.quantity * lineItem.merchandise.price.amount}
          </FormattedPrice>
        </Text>
      </div>
    })}
    {!notes ? null : <Text className="Cart__lineItemNotes" styleVariant="body3">{notes}</Text>}
    <div className="Cart__lineItemBottom">
      <LoadingConsumer>
        {({addLoad, removeLoad}) => <>
          <Counter
            min={1}
            max={99}
            onDecrement={() => {
              updateLineItem(
                addLoad, removeLoad, 'lineItemDecrement',
                cartLinesUpdate, updateCart,
                cartId, lineItems, -1
              )
            }}
            onIncrement={() => {
              updateLineItem(
                addLoad, removeLoad, 'lineItemIncrement',
                cartLinesUpdate, updateCart,
                cartId, lineItems, +1
              )
            }}>
            {quantity}
          </Counter>
          <LinkButton
            className="Cart__lineItemRemove"
            onClick={() => {
              updateLineItem(
                addLoad, removeLoad, 'lineItemRemove',
                cartLinesUpdate, updateCart,
                cartId, lineItems, -quantity
              )
            }}>
            Remove
          </LinkButton>
        </>}
      </LoadingConsumer>
    </div>
  </div>
}

function updateLineItem(
  addLoad, removeLoad, loadId,
  cartLinesUpdate, updateCart,
  cartId, lineItems, quantityChange
) {
  addLoad(loadId)
  cartLinesUpdate({
    variables: {
      cartId: cartId,
      lines: lineItems.map(item => {
        return {
          id: item.id,
          quantity: item.quantity + quantityChange,
        }
      }),
    }
  }).then(res => {
    const data = res.data.cartLinesUpdate
    if (data.userErrors.length > 0) console.log(data.userErrors)
    updateCart(data.cart)
    removeLoad(loadId)
  })
}
